import { AlertTriangle, ShieldCheck, Target, Layers, TrendingUp, HeartHandshake, MapPinned, Info } from "lucide-react";
import { usePageMeta } from "@/lib/seo";
import { IMAGES, SEGMENTS, REVENUE, MILESTONES, RISKS, INVESTOR_INTERESTS } from "@/content/site";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, SectionHeader, CtaLink, IconTile } from "@/components/site/primitives";
import { EnquiryForm } from "@/components/site/EnquiryForm";

const OVERVIEW = [
  { icon: Target, title: "The market problem", body: "Rural communities face distance from qualified professionals, fragmented access to consultations, diagnostics and medicines, delayed treatment, travel costs and weak continuity of care." },
  { icon: Layers, title: "Proposed solution", body: "Community healthcare access points, supported by trained workers and digital tools, that coordinate care from licensed doctors, authorised labs, pharmacies and referral hospitals." },
  { icon: HeartHandshake, title: "Partnership-led operating model", body: "Asset-light by design: SwastSaathi coordinates rather than owns clinical capacity, keeping capital focused on access points and technology." },
];

const VALIDATE = ["Market demand", "Pricing & willingness to pay", "Provider participation", "Centre-level unit economics", "Scalability"];

export default function InvestorsPage() {
  usePageMeta({
    title: "Investors & Incubators — An Idea-Stage HealthTech Venture",
    description: "An honest overview of SwastSaathi for investors, incubators and mentors: the problem, proposed model, revenue streams, Bihar pilot, milestones and risks.",
    path: "/investors",
  });
  return (
    <>
      <PageHero
        testId="investors-hero"
        eyebrow="For investors, incubators & mentors"
        title="An honest, idea-stage bet on rural primary healthcare."
        intro="SwastSaathi is an idea-stage venture. We have no revenue, traction or funding to report yet — what we offer is a clearly defined problem, a partnership-led model and a disciplined plan to validate it through a focused pilot in Bihar."
        image={IMAGES.teleInvestors}
        imageAlt="A community health worker shows an elderly rural woman a video consultation with a doctor on a tablet"
      >
        <CtaLink to="#enquiry" testId="investors-discuss-cta">Discuss Investment or Incubation</CtaLink>
      </PageHero>

      <section data-testid="investors-validate" className="bg-white py-16">
        <div className="container-x">
          <Reveal className="flex flex-col gap-6 rounded-2xl border border-magenta/20 bg-magenta-100/40 p-6 sm:p-8 lg:flex-row lg:items-center">
            <div className="flex items-start gap-3 lg:w-1/3">
              <Info className="mt-1 h-5 w-5 shrink-0 text-magenta" />
              <p className="font-display text-lg font-semibold leading-snug text-plum">What still needs to be validated through execution</p>
            </div>
            <ul className="flex flex-wrap gap-2 lg:w-2/3">
              {VALIDATE.map((v) => <li key={v} className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-plum">{v}</li>)}
            </ul>
          </Reveal>
        </div>
      </section>

      <section data-testid="investors-overview" className="bg-white pb-20 lg:pb-28">
        <div className="container-x grid gap-5 md:grid-cols-3">
          {OVERVIEW.map((o, i) => (
            <Reveal key={o.title} delay={i * 0.07}>
              <article data-testid={`investor-overview-${i}`} className="card-soft h-full p-7">
                <IconTile icon={o.icon} tone={i === 1 ? "plum" : "teal"} />
                <h2 className="mt-6 text-xl font-semibold">{o.title}</h2>
                <p className="mt-3 leading-relaxed text-slate">{o.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="investors-segments" className="bg-mint py-20 lg:py-28">
        <div className="container-x grid gap-14 lg:grid-cols-12">
          <SectionHeader className="lg:col-span-4" eyebrow="Target customer segments" title="Who the model is designed to serve." />
          <div className="grid gap-4 sm:grid-cols-2 lg:col-span-8">
            {SEGMENTS.map((s, i) => (
              <Reveal key={s.title} delay={(i % 2) * 0.06}>
                <div data-testid={`segment-${i}`} className="h-full rounded-2xl bg-white p-6">
                  <h3 className="text-lg font-semibold">{s.title}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-slate">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="investors-revenue" className="bg-white py-20 lg:py-28">
        <div className="container-x">
          <SectionHeader eyebrow="Potential revenue streams" title="Multiple streams, each to be validated." intro="No financial forecasts are presented. Each stream is subject to applicable regulations, partner agreements and pilot evidence." />
          <div className="mt-12 overflow-hidden rounded-2xl border border-plum/10">
            {REVENUE.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.04}>
                <div className={`grid gap-2 p-5 sm:grid-cols-12 sm:gap-6 sm:p-6 ${i % 2 ? "bg-white" : "bg-mint/50"}`}>
                  <p className="font-display text-sm font-semibold text-magenta sm:col-span-1">0{i + 1}</p>
                  <h3 className="text-lg font-semibold sm:col-span-4">{r.title}</h3>
                  <p className="text-[15px] leading-relaxed text-slate sm:col-span-7">{r.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="investors-pilot" className="grain relative overflow-hidden bg-plum py-20 lg:py-28">
        <div className="container-x relative grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 text-white"><MapPinned className="h-6 w-6" /></span>
            <p className="eyebrow mt-8 text-teal-100">Proposed Bihar pilot</p>
            <h2 className="mt-4 text-3xl font-semibold leading-[1.1] text-white sm:text-4xl">Start small, learn fast, prove the economics.</h2>
            <p className="mt-5 leading-relaxed text-white/75">The pilot is intended to test the full patient journey in a focused area of Bihar with a limited number of access points and partners. Location, scale and timing will be confirmed after community discovery and regulatory review.</p>
          </Reveal>
          <div className="lg:col-span-7">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/60">Key validation milestones</p>
            <ol className="mt-5 space-y-3">
              {MILESTONES.map((m, i) => (
                <Reveal as="li" key={m} delay={i * 0.05}>
                  <div data-testid={`milestone-${i}`} className="flex gap-4 rounded-2xl border border-white/10 bg-white/5 p-5">
                    <span className="font-display text-sm font-semibold text-teal-100">M{i + 1}</span>
                    <p className="text-[15px] leading-relaxed text-white/85">{m}</p>
                  </div>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section data-testid="investors-risks" className="bg-white py-20 lg:py-28">
        <div className="container-x">
          <SectionHeader eyebrow="Risks & mitigation" title="The risks we see — and how we plan to address them." />
          <div className="mt-12 grid gap-4 md:grid-cols-2">
            {RISKS.map((r, i) => (
              <Reveal key={r.risk} delay={(i % 2) * 0.06}>
                <article data-testid={`risk-${i}`} className="card-soft grid h-full gap-4 p-6 sm:grid-cols-2">
                  <div className="flex gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-magenta" strokeWidth={1.8} />
                    <div><p className="text-xs font-semibold uppercase tracking-wider text-slate">Risk</p><h3 className="mt-1 text-base font-semibold leading-snug">{r.risk}</h3></div>
                  </div>
                  <div className="flex gap-3 border-t border-plum/10 pt-4 sm:border-l sm:border-t-0 sm:pl-4 sm:pt-0">
                    <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-teal-700" strokeWidth={1.8} />
                    <div><p className="text-xs font-semibold uppercase tracking-wider text-slate">Mitigation</p><p className="mt-1 text-[15px] leading-relaxed text-ink">{r.mitigation}</p></div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="investors-thesis" className="bg-mint py-20 lg:py-28">
        <div className="container-x grid gap-6 md:grid-cols-2">
          <Reveal>
            <div className="card-soft h-full p-8 sm:p-10">
              <IconTile icon={TrendingUp} />
              <h2 className="mt-6 text-2xl font-semibold">Long-term scalability thesis</h2>
              <p className="mt-4 leading-relaxed text-slate">If a single centre can be shown to be trusted by its community and sustainable on its own economics, the partnership-led, asset-light design could allow the model to be replicated district by district — using shared technology, standard procedures and local partners rather than heavy capital investment. This thesis is unproven and is precisely what the pilot is designed to test.</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="card-soft h-full p-8 sm:p-10">
              <IconTile icon={HeartHandshake} tone="plum" />
              <h2 className="mt-6 text-2xl font-semibold">Intended social impact</h2>
              <p className="mt-4 leading-relaxed text-slate">Better access to primary care, less avoidable travel and lost income, earlier identification of health risks, improved continuity of care and stronger links between communities and providers — measured through clear indicators once the pilot begins.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="enquiry" data-testid="investors-enquiry" className="scroll-mt-24 bg-white py-20 lg:py-28">
        <div className="container-x grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHeader eyebrow="Discuss investment or incubation" title="Let's talk about building this together." intro="For early-stage investors, incubators, accelerators, startup competitions and mentors interested in healthcare access in emerging markets." />
            <Reveal delay={0.1} className="mt-8 rounded-2xl border border-plum/10 bg-plum-50 p-6 text-sm leading-relaxed text-plum">
              Submitting this form is an enquiry only and does not constitute an offer, commitment or agreement of any kind.
            </Reveal>
          </div>
          <Reveal className="card-soft p-6 sm:p-10 lg:col-span-8">
            <EnquiryForm mode="investment" idPrefix="investor" interests={INVESTOR_INTERESTS} interestLabel="Area of interest" />
          </Reveal>
        </div>
      </section>
    </>
  );
}
